import PropTypes from 'prop-types';
import { useState } from 'react';
import {
	DndContext as DndKitContext,
	DragOverlay,
	closestCenter,
	rectIntersection,
	pointerWithin,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
	useDroppable,
	defaultDropAnimationSideEffects,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';

import './DndContext.css';
import { buildClass } from '../../libs/classes';

/**
 * Storage Return Overlay Component
 */
const StorageReturnOverlay = () => {
	const { isOver, setNodeRef } = useDroppable({
		id: 'storage-return-overlay',
	});

	return (
		<div className={buildClass('storage-return-overlay').add(isOver, 'over')} ref={setNodeRef}>
			{isOver ? '✓ Drop here to return to storage' : '📦 Drop card here to return to storage'}
		</div>
	);
};

/**
 * Wrapper component for dnd-kit's DndContext to provide a similar API to react-beautiful-dnd's DragDropContext
 * @component
 */
const DndContext = ({ children, onDragEnd, onDragStart, onDragOver, renderDragOverlay }) => {
	const [activeId, setActiveId] = useState(null);
	const [activeData, setActiveData] = useState(null);
	const [showStorageOverlay, setShowStorageOverlay] = useState(false);
	const [storageContainerRect, setStorageContainerRect] = useState(null);

	// Custom collision detection that prioritizes actual pointer overlap
	const customCollisionDetection = (args) => {
		// First try pointer-based detection for more precise hovering
		const pointerCollisions = pointerWithin(args);
		if (pointerCollisions.length > 0) {
			return pointerCollisions;
		}

		// If no pointer collisions, fall back to rectangle intersection
		const intersectionCollisions = rectIntersection(args);
		if (intersectionCollisions.length > 0) {
			return intersectionCollisions;
		}

		// Finally, use closest center as fallback
		return closestCenter(args);
	};

	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: 8,
			},
		}),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		})
	);

	const handleDragEnd = (event) => {
		const { active, over } = event;

		// Hide storage overlay regardless of drop outcome
		setShowStorageOverlay(false);
		setStorageContainerRect(null);

		setActiveId(null);
		setActiveData(null);

		// Handle drop to storage overlay
		if (over && over.id === 'storage-return-overlay') {
			// Convert storage overlay drop to collection drop
			onDragEnd?.({
				draggableId: active.id,
				source: {
					droppableId:
						active.data.current?.sortable?.containerId || active.data.current?.droppableId || 'unknown',
					index: active.data.current?.sortable?.index || 0,
				},
				destination: {
					droppableId: 'collection',
					index: 0,
				},
			});
			return;
		}

		if (!over) {
			onDragEnd?.({
				draggableId: active.id,
				source: {
					droppableId: active.data.current?.sortable?.containerId || 'unknown',
					index: active.data.current?.sortable?.index || 0,
				},
				destination: null,
			});
			return;
		}

		const result = {
			draggableId: active.id,
			source: {
				droppableId:
					active.data.current?.sortable?.containerId || active.data.current?.droppableId || 'unknown',
				index: active.data.current?.sortable?.index || 0,
			},
			destination: {
				droppableId: over.data.current?.sortable?.containerId || over.id,
				index: over.data.current?.sortable?.index || 0,
			},
		};

		onDragEnd?.(result);
	};

	const handleDragStart = (event) => {
		const { active } = event;

		// Check if dragging from storage container
		// Try multiple ways to find the draggable element and storage container
		let activeElement = document.querySelector(`[data-dnd-kit-draggable-id="${active.id}"]`);
		if (!activeElement) {
			// Fallback: try to find by ID attribute
			activeElement = document.getElementById(active.id);
		}
		if (!activeElement) {
			// Fallback: try to find any drag-tile element (since drag just started)
			activeElement = document.querySelector('.drag-tile[data-dnd-kit-draggable-id]');
		}

		console.log('DragStart - Active ID:', active.id);
		console.log('DragStart - Active element:', activeElement);

		// Look for storage container
		let storageContainer = activeElement?.closest('.sample-list-content');
		if (!storageContainer) {
			// Alternative approach: look for any .sample-list-content that contains draggable items
			storageContainer = document.querySelector('.sample-list-content');
		}

		console.log('DragStart - Storage container found:', storageContainer);

		if (storageContainer) {
			// Get the position and size of the storage container
			const rect = storageContainer.getBoundingClientRect();
			console.log('DragStart - Container rect:', rect);
			setStorageContainerRect({
				top: rect.top,
				left: rect.left,
				width: rect.width,
				height: rect.height,
			});
			setShowStorageOverlay(true);
			console.log('DragStart - Overlay should be shown');
		} else {
			console.log('DragStart - No storage container found');
		}

		setActiveId(active.id);
		setActiveData(active.data.current);

		onDragStart?.({
			draggableId: active.id,
			source: {
				droppableId:
					active.data.current?.sortable?.containerId || active.data.current?.droppableId || 'unknown',
				index: active.data.current?.sortable?.index || 0,
			},
		});
	};

	const handleDragOver = (event) => {
		const { active, over } = event;

		if (!over) return;

		onDragOver?.({
			draggableId: active.id,
			source: {
				droppableId:
					active.data.current?.sortable?.containerId || active.data.current?.droppableId || 'unknown',
				index: active.data.current?.sortable?.index || 0,
			},
			destination: {
				droppableId: over.data.current?.sortable?.containerId || over.id,
				index: over.data.current?.sortable?.index || 0,
			},
		});
	};

	const renderActiveItem = () => {
		if (!activeId) return null;

		if (renderDragOverlay) {
			return renderDragOverlay(activeId, activeData);
		}

		return <div className="default-drag-overlay">Dragging...</div>;
	};

	return (
		<div className={buildClass('dnd-context-wrapper').add(activeId, 'dragging')}>
			<DndKitContext
				collisionDetection={customCollisionDetection}
				sensors={sensors}
				onDragEnd={handleDragEnd}
				onDragOver={handleDragOver}
				onDragStart={handleDragStart}
			>
				{children}

				{/* Storage Return Overlay - render before DragOverlay so dragging tile appears above */}
				{(() => {
					console.log('Overlay render - showStorageOverlay:', showStorageOverlay);
					console.log('Overlay render - storageContainerRect:', storageContainerRect);
					return showStorageOverlay && storageContainerRect ? (
						<div
							className="storage-return-overlay-container"
							style={{
								top: storageContainerRect.top,
								left: storageContainerRect.left,
								width: storageContainerRect.width,
								height: storageContainerRect.height,
							}}
						>
							<StorageReturnOverlay />
						</div>
					) : null;
				})()}

				<DragOverlay
					dropAnimation={{
						duration: 300,
						// easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)',
						easing: 'ease-out',
						sideEffects: defaultDropAnimationSideEffects({
							// Keep the original item visible during animation
							styles: {
								active: {
									opacity: '1',
								},
							},
						}),
					}}
					style={{ zIndex: 9999 }}
				>
					{renderActiveItem()}
				</DragOverlay>
			</DndKitContext>
		</div>
	);
};

DndContext.propTypes = {
	children: PropTypes.node.isRequired,
	onDragEnd: PropTypes.func,
	onDragStart: PropTypes.func,
	onDragOver: PropTypes.func,
	renderDragOverlay: PropTypes.func,
};

export default DndContext;
